import type { UploadProgressPresentationStage } from '@sosocio/creator-types/app';
import ButtonComponent from 'components/button';
import ProgressBar from 'components/progress-bar';
import SpinnerComponent from 'components/spinner';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import {
	Prop,
	Vue,
} from 'vue-facing-decorator';
import {
	getUploadProgressTextKey,
	isUploadProgressComplete,
	isUploadProgressIndeterminate,
	resolveUploadProgressStage,
} from './presentation';
import Template from './template.vue';

@Component({
	name: 'UploadProgressView',
	components: {
		SpinnerComponent,
		ProgressBar,
		ButtonComponent,
	},
	emits: [
		'cancel',
		'close',
		'startEditing',
	],
	mixins: [Template],
})
class UploadProgressView extends Vue {
	@Prop({
		required: true,
		type: Number,
	})
	public progressPercentage!: number;

	@Prop({
		required: true,
		type: Number,
	})
	public submittedCount!: number;

	@Prop({
		default: () => [],
		type: Array,
	})
	public photos!: string[];

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly standalone!: boolean;

	@Prop({
		type: String,
	})
	public readonly stage?: UploadProgressPresentationStage;

	protected get currentStage(): UploadProgressPresentationStage {
		return resolveUploadProgressStage(
			this.stage,
			this.progressPercentage,
		);
	}

	protected get uploadComplete(): boolean {
		return isUploadProgressComplete(this.currentStage);
	}

	protected get isIndeterminateStage(): boolean {
		return isUploadProgressIndeterminate(this.currentStage);
	}

	protected get headerText(): string {
		let text = 'views.uploadProgress.header';

		if (this.uploadComplete) {
			text = 'views.uploadProgress.header_complete';
		} else if (
			this.progressPercentage >= 90
			|| this.isIndeterminateStage
		) {
			text = 'views.uploadProgress.header_almost';
		}

		return this.$t(
			text,
			{
				count: this.submittedCount,
			},
		);
	}

	protected get progressText(): string {
		return this.$t(getUploadProgressTextKey(
			this.currentStage,
			this.progressPercentage,
		));
	}

	protected onCancelClick() {
		this.$emit('cancel');
	}

	protected onCloseClick() {
		this.$emit('close');
	}

	protected onStartEditingClick(): void {
		this.$emit('startEditing');
	}
}

export default toNative(UploadProgressView);
